/**
 * Dialog Upload Relevé + Review OCR + Import — Phase 2.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Upload, Loader2, FileText, AlertTriangle, CheckCircle2, X, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TX_TYPE_OPTIONS = [
  'virement_entrant', 'virement_sortant', 'cheque_emis', 'cheque_recu',
  'prelevement', 'frais_bancaires', 'agios', 'commission',
  'depot_especes', 'retrait', 'virement_interne', 'autre',
];

const fmt = (v) => (v == null || isNaN(v) ? '—' : Number(v).toLocaleString('fr-FR'));

const StatementUploadDialog = ({ open, onClose, accountId, accountLabel, onImported }) => {
  const [step, setStep] = useState('upload');   // 'upload' | 'review'
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);   // { statement, extraction, integrity }
  const [lines, setLines] = useState([]);

  const reset = () => {
    setStep('upload'); setFile(null); setResult(null); setLines([]);
  };

  const closeAll = () => { reset(); onClose(); };

  const doUpload = async () => {
    if (!file) { toast({ title: 'Sélectionnez un fichier', variant: 'destructive' }); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('account_id', accountId);
      fd.append('file', file);
      const r = await axios.post(`${API}/bank/statements/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setResult(r.data);
      const rawLines = (r.data.extraction?.lines || []).map((li) => ({
        ...li,
        skip: !!li.is_duplicate,
      }));
      setLines(rawLines);
      setStep('review');
    } catch (e) {
      toast({
        title: 'OCR indisponible',
        description: e?.response?.data?.detail || 'Erreur',
        variant: 'destructive',
      });
    } finally { setUploading(false); }
  };

  const doImport = async () => {
    if (!result?.statement?.id) return;
    setImporting(true);
    try {
      const r = await axios.post(
        `${API}/bank/statements/${result.statement.id}/import`,
        { lines },
      );
      toast({ title: `${r.data.imported} opération(s) importée(s)`, description: r.data.skipped ? `${r.data.skipped} ignorée(s)` : undefined });
      onImported?.();
      closeAll();
    } catch (e) {
      toast({ title: 'Import échoué', description: e?.response?.data?.detail, variant: 'destructive' });
    } finally { setImporting(false); }
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((li, i) => (i === idx ? { ...li, ...patch } : li)));
  };

  const integrity = result?.integrity;
  const extract = result?.extraction || {};

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeAll()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="bank-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-blue-600" />
            {step === 'upload' ? 'Importer un relevé bancaire' : 'Vérifier et valider les lignes'}
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 mt-2" data-testid="upload-step">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                Compte : <strong>{accountLabel}</strong>. Formats acceptés : PDF, JPG, PNG, HEIC. Max 15 MB.
                L&apos;OCR détecte automatiquement les lignes (Date · Libellé · Débit/Crédit · Solde).
                Vous pourrez corriger avant d&apos;importer.
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Fichier du relevé</Label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-zinc-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                data-testid="upload-file-input"
              />
              {file && (
                <p className="text-xs text-zinc-500 mt-1">
                  <FileText className="inline h-3 w-3 mr-1" />
                  {file.name} · {(file.size / 1024).toFixed(0)} KB
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={closeAll}>Annuler</Button>
              <Button onClick={doUpload} disabled={!file || uploading}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="upload-submit-btn">
                {uploading ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Extraction OCR (15-30s)…</>
                ) : (
                  <><Upload className="h-4 w-4 mr-1.5" />Lancer l&apos;OCR</>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-4 mt-2" data-testid="review-step">
            {/* Header extraction */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border border-zinc-200 rounded-lg p-3">
                <div className="text-[10px] uppercase font-bold text-zinc-500">Banque détectée</div>
                <div className="text-sm font-semibold text-zinc-900">{extract.bank_name || '—'}</div>
                <div className="text-[11px] text-zinc-500 font-mono">{extract.account_number || '—'}</div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-lg p-3">
                <div className="text-[10px] uppercase font-bold text-zinc-500">Période</div>
                <div className="text-sm font-semibold text-zinc-900">
                  {extract.period_start || '?'} → {extract.period_end || '?'}
                </div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-lg p-3">
                <div className="text-[10px] uppercase font-bold text-zinc-500">Solde ouverture</div>
                <div className="text-sm font-semibold text-zinc-900 tabular-nums">
                  {fmt(extract.opening_balance)} XOF
                </div>
              </div>
              <div className="bg-white border border-zinc-200 rounded-lg p-3">
                <div className="text-[10px] uppercase font-bold text-zinc-500">Solde clôture</div>
                <div className="text-sm font-semibold text-zinc-900 tabular-nums">
                  {fmt(extract.closing_balance)} XOF
                </div>
              </div>
            </div>

            {/* Contrôle d'intégrité */}
            {integrity && integrity.ok !== null && (
              <div className={`rounded-lg border p-3 flex items-start gap-2.5 ${
                integrity.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'
              }`} data-testid="integrity-check">
                {integrity.ok ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 text-xs">
                  <div className={`font-semibold ${integrity.ok ? 'text-emerald-900' : 'text-rose-900'}`}>
                    {integrity.ok ? 'Contrôle d\'intégrité OK' : `Écart détecté : ${fmt(integrity.gap)} XOF`}
                  </div>
                  <div className={integrity.ok ? 'text-emerald-700' : 'text-rose-700'}>
                    Ouverture {fmt(integrity.opening)} + Crédits {fmt(integrity.total_credits)} − Débits {fmt(integrity.total_debits)} = <strong>{fmt(integrity.expected_closing)}</strong>
                    {' vs '}Clôture déclarée <strong>{fmt(integrity.closing)}</strong>
                  </div>
                  {!integrity.ok && (
                    <div className="text-rose-800 mt-1 font-medium">
                      ⚠️ Vérifiez et corrigez les lignes avant import. Un écart peut indiquer une ligne manquante ou mal lue par l&apos;OCR.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tableau lignes éditables */}
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-zinc-50 border-b flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-zinc-600">
                  {lines.length} ligne{lines.length > 1 ? 's' : ''} extraite{lines.length > 1 ? 's' : ''} · Éditez si nécessaire
                </span>
                <Badge variant="outline" className="text-[10px]">
                  {lines.filter((l) => l.skip).length} ignorée(s) · {lines.filter((l) => l.is_duplicate).length} doublon(s) détecté(s)
                </Badge>
              </div>
              <div className="overflow-x-auto max-h-[400px]">
                <table className="w-full text-xs" data-testid="review-table">
                  <thead className="bg-zinc-50/70 sticky top-0">
                    <tr className="border-b text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                      <th className="px-2 py-2 w-8">✓</th>
                      <th className="px-2 py-2">Date</th>
                      <th className="px-2 py-2">Libellé</th>
                      <th className="px-2 py-2">Type</th>
                      <th className="px-2 py-2 text-right">Débit</th>
                      <th className="px-2 py-2 text-right">Crédit</th>
                      <th className="px-2 py-2 text-right">Solde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((li, idx) => (
                      <tr key={idx} className={`border-b border-zinc-50 hover:bg-blue-50/30 ${li.skip ? 'opacity-40 line-through' : ''} ${li.is_duplicate ? 'bg-amber-50/40' : ''}`}
                        data-testid={`review-row-${idx}`}>
                        <td className="px-2 py-1.5">
                          <input type="checkbox" checked={!li.skip}
                            onChange={(e) => updateLine(idx, { skip: !e.target.checked })}
                            className="cursor-pointer"
                            data-testid={`review-check-${idx}`} />
                        </td>
                        <td className="px-2 py-1">
                          <Input type="date" value={li.operation_date || ''}
                            onChange={(e) => updateLine(idx, { operation_date: e.target.value })}
                            className="h-7 text-xs w-[130px]" />
                        </td>
                        <td className="px-2 py-1">
                          <Input value={li.label || ''}
                            onChange={(e) => updateLine(idx, { label: e.target.value })}
                            className="h-7 text-xs" />
                          {li.is_duplicate && (
                            <span className="text-[9px] text-amber-700 font-semibold">DOUBLON DÉTECTÉ</span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <select value={li.detected_type || 'autre'}
                            onChange={(e) => updateLine(idx, { detected_type: e.target.value })}
                            className="h-7 text-xs rounded border border-zinc-200 px-1 w-[130px]">
                            {TX_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" value={li.debit ?? ''}
                            onChange={(e) => updateLine(idx, { debit: e.target.value ? Number(e.target.value) : null, credit: null })}
                            className="h-7 text-xs tabular-nums text-right w-[110px]" placeholder="—" />
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" value={li.credit ?? ''}
                            onChange={(e) => updateLine(idx, { credit: e.target.value ? Number(e.target.value) : null, debit: null })}
                            className="h-7 text-xs tabular-nums text-right w-[110px]" placeholder="—" />
                        </td>
                        <td className="px-2 py-1">
                          <Input type="number" value={li.balance ?? ''}
                            onChange={(e) => updateLine(idx, { balance: e.target.value ? Number(e.target.value) : null })}
                            className="h-7 text-xs tabular-nums text-right w-[110px]" placeholder="—" />
                        </td>
                      </tr>
                    ))}
                    {lines.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-6 text-zinc-400">Aucune ligne extraite.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t">
              <button
                onClick={() => setStep('upload')}
                className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
                data-testid="review-back-btn"
              >
                <X className="h-3 w-3" /> Annuler et re-uploader
              </button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={closeAll}>Fermer</Button>
                <Button onClick={doImport} disabled={importing || lines.every((l) => l.skip)}
                  className="bg-[#16A34A] hover:bg-[#15803d] text-white" data-testid="review-import-btn">
                  {importing ? (
                    <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Import…</>
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 mr-1.5" />Importer {lines.filter((l) => !l.skip).length} ligne(s)</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatementUploadDialog;
